/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    Box,
    Flex,
    FormControl, // Chakra FormControl with role="group"
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    useStyleConfig,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownIcon} from '../custom-icons'
import FlagIcon from '../../components/flag-icons'
import {useIntl} from 'react-intl'
import {OPTIONAL_INDICATOR_TEXT} from '../../constants'
import useCountryList from '../../hooks/use-country-list'
import {findDialCodeByCountry} from '../../utils/utils'
import {COUNTRY_TRANSLATIONS} from '../forms/country-list'

import {CustomRequiredIndicator} from '../../components/field'

const PhoneField = ({
    name,
    // example case: c_phoneCountry on checkout AddressFields
    phoneName = `${name}Country`,
    label,
    formLabel,
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    trigger,
    setValue,
    clearErrors,
    autoComplete,
    defaultCountry = 'XX',
    defaultValue,
    helpText,
    isDisabled,
    hasIndicator = true,
    isCountryListSorted = true,
    children
}) => {
    const styles = useStyleConfig('PhoneField')
    const {formatMessage} = useIntl()
    const countries = useCountryList({isSorted: isCountryListSorted, hasDial: true})
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(() => () => true)

    const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountry)

    const getInputProps = ({onChange}) => ({
        inputMode: 'numeric',
        onChange: (event) => {
            const numericValue = event.target.value.replace(/\D/g, '')
            onChange(numericValue)
        }
    })

    /**
     * Validates a phone number based on the provided country code.
     *
     * This function checks if the given phone number is valid for the specified country.
     * It does not perform validation if the phone number is empty, which is useful for
     * optional phone fields. Additionally, it bypasses validation for a specific country
     * code ('XX'), which may represent a special case or placeholder.
     *
     * @param {string} value - The phone number to be validated.
     * @param {string} countryCode - The ISO 3166-1 alpha-2 country code representing the country
     *                               for which the phone number should be validated.
     * @returns {boolean|string} - Returns `true` if the phone number is valid or if validation
     *                             is bypassed. Returns an error message string if the phone
     *                             number is invalid.
     */
    const validatePhoneNumber = (value, countryCode) => {
        // Do not validate if the value is empty: important for optional phone field
        if (!value) {
            return true
        }

        if (countryCode === 'XX') {
            return true
        }

        const countryDialPhone = findDialCodeByCountry(countryCode) + value
        const countryName = formatMessage(COUNTRY_TRANSLATIONS[countryCode])
        const invalidPhoneMessage = formatMessage(
            {
                defaultMessage: 'Invalid phone number for {country}',
                id: 'phone_field.invalid_phone'
            },
            {country: countryName}
        )

        if (typeof isValidPhoneNumber === 'function') {
            return isValidPhoneNumber(countryDialPhone) || invalidPhoneMessage
        } else {
            return invalidPhoneMessage
        }
    }

    useEffect(() => {
        // Imports the react-phone-number-input library asynchronously
        const loadPhoneNumberUtils = async () => {
            const {isValidPhoneNumber: validatePhone} = await import('react-phone-number-input')
            setIsValidPhoneNumber(() => validatePhone)
        }

        loadPhoneNumberUtils()
    }, [])

    // Update the country dial select value when defaultCountry changes
    useEffect(() => {
        if (defaultCountry && setValue) {
            let phoneCountry = defaultCountry
            // Canary Islands (IC) is not a country, so we use Spain (ES) instead
            if (defaultCountry === 'IC') {
                phoneCountry = 'ES'
            }
            setValue(phoneName, phoneCountry)
            setSelectedCountryCode(phoneCountry)
            clearErrors(name)
        }
    }, [defaultCountry, setValue, name])

    /**
     * Handles the paste event for the phone input field by sanitizing the pasted data.
     *
     * This function prevents the default paste behavior, extracts the pasted text,
     * removes the country dial code prefix (if present), strips all non-numeric characters,
     * and updates the input value accordingly. It then updates the form state and triggers
     * validation for the specified field.
     *
     * @param {ClipboardEvent} event - The paste event object from the input field.
     */
    const onPaste = (event) => {
        // Remove prefix from pasted number
        event.preventDefault()
        const pastedData = event.clipboardData.getData('Text')
        const numericValue = pastedData
            .replace(findDialCodeByCountry(selectedCountryCode) || '+XX', '')
            .replace(/\D/g, '')
        event.target.value = numericValue
        setValue(name, numericValue)
        trigger(name)
    }

    return (
        <FormControl
            {...styles.container}
            variant="floating"
            id={name}
            isInvalid={error}
            isDisabled={isDisabled}
            isRequired={Object.prototype.hasOwnProperty.call(rules, 'required')}
        >
            <Flex>
                <Controller
                    name={phoneName}
                    control={control}
                    rules={rules}
                    render={({field: {onChange, value, ref}}) => {
                        const currentCountryCode = value || selectedCountryCode
                        const currentCountryLabel =
                            countries.find((country) => country.value === currentCountryCode)
                                ?.label || 'XX' // Unknown flag

                        return (
                            <Box {...styles.countryContainer}>
                                <Box {...styles.countrySelected}>
                                    <FlagIcon
                                        countryCode={currentCountryCode}
                                        aria-label={currentCountryLabel}
                                        boxSize={8}
                                    />
                                </Box>
                                <VisuallyHidden>
                                    <FormLabel
                                        htmlFor={`${name}-country`}
                                        id={`${name}-country-label`}
                                    >
                                        {formatMessage({
                                            defaultMessage: 'Phone number country',
                                            id: 'phone_field.country_select.label'
                                        })}
                                    </FormLabel>
                                </VisuallyHidden>
                                <Select
                                    id={`${name}-country`}
                                    icon={<ChevronDownIcon />}
                                    variant="basic"
                                    ref={ref}
                                    autoComplete="country"
                                    onChange={(e) => {
                                        onChange(e)
                                        setSelectedCountryCode(e.target.value)
                                        clearErrors(name)
                                    }}
                                    value={selectedCountryCode}
                                    placeholder=""
                                    {...styles.countries}
                                >
                                    {countries.map((opt) => {
                                        const dialCode = findDialCodeByCountry(opt.value)
                                        const labelWithDialCode = `${opt.label} ${dialCode}`
                                        return (
                                            <option key={opt.value} value={opt.value}>
                                                {labelWithDialCode}
                                            </option>
                                        )
                                    })}
                                </Select>
                            </Box>
                        )
                    }}
                />
                <Controller
                    name={name}
                    control={control}
                    rules={{
                        ...rules,
                        validate: (value) => {
                            const phoneValidation = validatePhoneNumber(value, selectedCountryCode)
                            if (typeof rules.validate === 'function') {
                                return rules.validate(value) && phoneValidation
                            }
                            return phoneValidation
                        }
                    }}
                    defaultValue={defaultValue}
                    render={({field: {onChange, value, ref}}) => {
                        const _inputProps =
                            typeof inputProps === 'function'
                                ? inputProps({value, onChange})
                                : getInputProps({onChange})
                        const valueWithoutPrefix = value
                            ?.replace(findDialCodeByCountry(selectedCountryCode) || '+XX', '')
                            .replace(/\s/g, '')

                        return (
                            <>
                                <InputGroup data-group={undefined}>
                                    <InputLeftElement pointerEvents="none" {...styles.dial}>
                                        {findDialCodeByCountry(selectedCountryCode) || '+XX'}
                                    </InputLeftElement>
                                    <Input
                                        variant="basic"
                                        ref={ref}
                                        onChange={onChange}
                                        onPaste={onPaste}
                                        value={valueWithoutPrefix}
                                        type="tel"
                                        placeholder={placeholder || ''}
                                        autoComplete={autoComplete}
                                        {...styles.phone}
                                        {..._inputProps}
                                    />
                                    {children}
                                </InputGroup>
                                {formLabel || (
                                    <FormLabel
                                        requiredIndicator={<CustomRequiredIndicator />}
                                        {...(hasIndicator && {
                                            optionalIndicator:
                                                ' ' + formatMessage(OPTIONAL_INDICATOR_TEXT)
                                        })}
                                        {...styles.label}
                                    >
                                        {label}
                                    </FormLabel>
                                )}
                            </>
                        )
                    }}
                />
            </Flex>
            {error && <FormErrorMessage>{error.message}</FormErrorMessage>}

            {helpText}
        </FormControl>
    )
}

PhoneField.propTypes = {
    name: PropTypes.string,
    phoneName: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    countries: PropTypes.arrayOf(PropTypes.shape({icon: PropTypes.object, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    trigger: PropTypes.func,
    setValue: PropTypes.func,
    clearErrors: PropTypes.func,
    defaultCountry: PropTypes.any,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    isDisabled: PropTypes.bool,
    hasIndicator: PropTypes.bool,
    isCountryListSorted: PropTypes.bool,
    children: PropTypes.any
}

export default PhoneField
